import { useEffect, useState } from 'react'
import { Typography } from '@material-ui/core'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import { Formik, Form } from 'formik'
import * as Analytics from 'core/common/analytics'
import { EDatasheetFields } from 'core/common/constants/datasheet'
import i18n from 'core/common/i18n'
import { getErrorMessage, validateField } from 'core/common/utils/validation'
import { Card, CardContent, MenuOption, Button } from 'core/components/Core'
import TextField from 'core/components/TextField'
import { HelperTextWrapper } from 'core/components/TextField/style'
import { useFormContext } from 'core/pages/ProductNew-v2/context/ProductNewContext'
import ProductType from '../ProductDescription/components/ProductType'
import { warratiesPeriod } from './constants'
import { ProductCharacteristicsFormSchema } from './schema'
import * as S from './style'

const fieldTranslations = {
  brand: 'marca',
  garantia: 'garantia',
  modelo: 'modelo',
  type: 'tipo de produto',
}

const allowedFields = ['brand', 'garantia', 'modelo', 'type']

export const ProductCharacteristicsForm = ({
  onNext,
  isEditMode,
  onEditSubmit,
  custom,
  handleEditStep,
  step,
  formMode,
}) => {
  const { formData, setFormData } = useFormContext()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const initialValues = {
    brand: formData.brand,
    modelo: formData.modelo,
    garantia: formData.garantia,
    gender: formData.gender,
    type: formData.type,
  }

  useEffect(() => {
    const submitEdit = async () => {
      if (isSubmitting && isEditMode && onEditSubmit) {
        setIsSubmitting(false)
        setIsLoading(true)
        try {
          await onEditSubmit(formData)
          setIsLoading(false)
        } catch (error) {
          console.error('Error during API call', error)
        } finally {
          setIsLoading(false)
        }
      }
    }

    submitEdit()
  }, [isSubmitting, formData, isEditMode, onEditSubmit])

  const onSubmit = values => {
    Analytics.ProductCharacteristicsSaved(formMode)
    setFormData(prevData => ({
      ...prevData,
      brand: values?.brand?.trim(),
      modelo: values?.modelo?.trim(),
      garantia: values?.garantia?.trim(),
      gender: values?.gender?.trim(),
      type: values?.type?.trim(),
    }))

    setIsSubmitting(true)
    onNext()
  }

  const handleValidation = async ({ validateForm }) => {
    const errors = await validateForm()

    Object.keys(errors).forEach(field => {
      if (allowedFields.includes(field)) {
        const translatedField = fieldTranslations[field] || field
        Analytics.ProductCharacteristicsValidationError(formMode, translatedField)
      }
    })
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={
        custom?.forms.schema ? custom?.forms.schema : ProductCharacteristicsFormSchema
      }
      enableReinitialize
    >
      {({
        handleSubmit,
        errors,
        touched,
        submitCount,
        handleBlur,
        handleChange,
        values,
        dirty,
        resetForm,
        validateForm,
      }) => {
        return (
          <Form className="w-100">
            <S.Container>
              <Card raised elevation={1} className="p-4">
                <CardContent className="p-0">
                  {!isEditMode && (
                    <Typography variant="h5" component="h2" className="mb-4">
                      {i18n.t('productNew.basicData.characteristicsTitle')}
                    </Typography>
                  )}

                  <div className="mb-6">
                    {custom?.forms.fields ? (
                      custom.forms.fields({
                        errors,
                        touched,
                        submitCount,
                        handleBlur,
                        values,
                        handleChange,
                        handleEditStep,
                        step,
                      })
                    ) : (
                      <>
                        <TextField
                          error={validateField('brand', errors, touched, submitCount > 0)}
                          onBlur={handleBlur}
                          variant="outlined"
                          helperText={getErrorMessage('brand', errors, touched)}
                          label="Marca"
                          name="brand"
                          value={values?.brand}
                          style={{ width: '45%' }}
                          sanitize
                          className="mr-3"
                          onChange={e => {
                            handleChange(e)
                            handleEditStep(step.key)
                          }}
                        />
                        <TextField
                          error={validateField('modelo', errors, touched, submitCount > 0)}
                          onBlur={handleBlur}
                          variant="outlined"
                          helperText={getErrorMessage('modelo', errors, touched)}
                          label="Modelo"
                          name={EDatasheetFields.Modelo}
                          value={values?.modelo}
                          style={{ width: '45%' }}
                          sanitize
                          onChange={e => {
                            handleChange(e)
                            handleEditStep(step.key)
                          }}
                        />
                        <div className="d-flex mt-4">
                          <div style={{ width: '45%' }} className="mr-3">
                            <TextField
                              error={validateField('garantia', errors, touched, submitCount > 0)}
                              onBlur={handleBlur}
                              variant="outlined"
                              helperText={getErrorMessage('garantia', errors, touched)}
                              label={i18n.t('productNew.basicData.warrantyLabelText')}
                              name={EDatasheetFields.Garantia}
                              value={values?.garantia}
                              hiddenLabel
                              fullWidth
                              select
                              onChange={e => {
                                handleChange(e)
                                handleEditStep(step.key)
                              }}
                            >
                              {warratiesPeriod?.map((warranty, index) => (
                                <MenuOption key={index} value={warranty.value}>
                                  {warranty.label}
                                </MenuOption>
                              ))}
                            </TextField>

                            <HelperTextWrapper>
                              <Typography
                                className={`${errors.garantia ? 'mt-3' : 'mt-1'} ml-3`}
                                align="center"
                                color="primary"
                                variant="caption"
                                style={{ fontWeight: 400 }}
                              >
                                <InfoOutlinedIcon color="disabled" fontSize="small" />
                                {i18n.t('productNew.basicData.warrantyHelperText')}
                              </Typography>
                            </HelperTextWrapper>
                          </div>

                          <div style={{ width: '45%' }}>
                            <ProductType onChange={() => handleEditStep(step.key)} />
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="w-100 d-flex justify-end">
                    {!isEditMode && (
                      <>
                        <Button
                          variant="text"
                          color="primary"
                          onClick={() => {
                            resetForm()
                            handleEditStep(null)
                          }}
                          className="mr-2"
                        >
                          <span>Cancelar</span>
                        </Button>

                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            handleSubmit()
                            handleValidation({ validateForm })
                          }}
                        >
                          {i18n.t('productNew.actions.continue')}
                        </Button>
                      </>
                    )}

                    {isEditMode && (dirty || isLoading) && (
                      <>
                        <Button
                          variant="text"
                          color="primary"
                          onClick={() => resetForm()}
                          className="mr-2"
                        >
                          <span>Cancelar</span>
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            handleSubmit()
                            handleValidation({ validateForm })
                          }}
                          loading={isLoading}
                        >
                          <span>Salvar</span>
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            </S.Container>
          </Form>
        )
      }}
    </Formik>
  )
}
